<script setup>
// 1. Imports
import { computed, nextTick, onMounted, onUnmounted, onBeforeMount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "@/store";
import { useAuthStore } from "@/store/auth";
import api from "@/api/api";
import { ENDPOINTS } from "@/api/constants";
import { t } from "@/utils/i18n";

// Icons
import IChevron from "@/assets/img/icon/i-chevron.vue";
import ITrash from "@/assets/img/icon/i-trash.vue";
import IDownload from "@/assets/img/icon/i-download.vue";
import ISetting from "@/assets/img/icon/i-setting.vue";
import ICopy from "@/assets/img/icon/i-copy.vue";
import ISynchronize from "@/assets/img/icon/i-synchronize.vue";
import IClose from "@/assets/img/icon/i-close.vue";
import IVariant from "@/assets/img/icon/i-variant.vue";
import IParaphrase from "@/assets/img/icon/i-paraphrase.vue";
import ICheck from "@/assets/img/icon/i-check.vue";
import ISearch from "@/assets/img/icon/i-search.vue";
import IPlus from "@/assets/img/icon/i-plus.vue";

// 2. Props
const props = defineProps({
	window: {
		type: Object,
		required: false,
	},
});

// 3. Stores & Router
const store = useStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

// 4. Reactive State & Variable
const projectId = route.query.projectId;
const projectInfo = ref({});
const srcLangName = ref('');
const tgtLangName = ref('');
const fieldName = ref('');
const translationModelName = ref('');

const projectSentences = ref([]);
const copiedProjectSentences = ref([]);
const originalTables = ref([]);
const translationTables = ref([]);
const textareaRefs = ref([]);

const glossaryData = ref([]);
const copiedGlossaryData = ref([]);
const copiedReferenceTarget = ref([-1, -1]);
const addedGlossaryData = ref([]);
const lastGloId = ref(1);
const gloUpdateId = ref(0);
const gloTextareaRefs = ref([]);

const preProjectTextInput = ref('');
const projectTextInput = ref('');
const gloTextOriginalInput = ref('');
const gloTextTranslationInput = ref('');

const currentProgressNum = ref(0);
const timeoutId = ref(null);

const maxTableRender = ref(5);
const lastProjectTextId = ref(50);
const projectSentencesObserverTarget = ref(null);
const gloObserverTarget = ref(null);
let projectSentencesObserver;
let gloObserver;

const formData = ref({
	tamseCode: 'A-1',
	sourceLangId: 0,
	targetLangId: 0,
	publicPromptId: 1,
	translationModelId: 3,
	have_private_prompt: false,
	private_prompt: '',
	projectFieldId: 1,
	displayName: '',
	documentType: '',
	promptMode: 'segment',
	promptName: '',
	promptContents: ''
})

const flags = ref({
	downloadHover: false,
	isTranslated: false,
	modalOnOff: false,
	deleteProject: false,
	loadingCompareEngine: false,
	compareEngineOnOff: false,
	gloDeleteMode: false,
	gloEditMode: false,
	savingGlo: -1,
	applyEditedGlossaryData: false,
	firstGloDetected: false,
	paraphraseCopy: false,
	paraphraseCopyTooltip: false,
	translated: false,
	paraphraseAll: false,
	paraphraseAllTooltip: false,
});


// 5. Computed
const filteredProjectSentences = computed(() => {
	if (!projectSentences.value.length) return false;

	const keyword = projectTextInput.value.toLowerCase();
	return projectSentences.value.filter(({ original_text, latest_text, translated_text_korean, polished_text }) => {
		return (
			(original_text || '').toLowerCase().includes(keyword) ||
			(latest_text || '').toLowerCase().includes(keyword) ||
			(translated_text_korean || '').toLowerCase().includes(keyword) ||
			(polished_text || '').toLowerCase().includes(keyword)
		);
	});
});

const filteredAddedGlossaryData = computed(() => {
	if (!addedGlossaryData.value.length) return false;
	return addedGlossaryData.value.slice().reverse();
})

// 6. Methods
function langNameById(id) {
	const item = store.languageListDefault.find(item => item.id === id);
	if (!item) return "";
	return localStorage.getItem("userLanguage") === 'ko' ? item.name : item.parameter;
}

function fieldNameById(id) {
	const item = store.fieldListDefault.find(item => item.id === id);
	if (!item) return "";
	return localStorage.getItem("userLanguage") === 'ko' ? item.name : item.parameter;
}

function translationModelNameById(id) {
	const item = store.translationModelListDefault.find(item => item.id === id);
	
	if (!item) return "";
	else if(item.is_deprecated) return `${item.model_name}${ t('(삭제됨)', 'Deleted')}`;
	return item.model_name;
}

function checkTranslationPartialLoading(ps) {
	ps.isHovered = !!ps.loading;
}

function checkNotSavedStatus(ps) {
	ps.isNotSaved = ps.latest_text !== copiedProjectSentences.value[ps.sentence_id].latest_text;
}

function handleMouseEnter(ps) {
	ps.isHovered = true;
	ps.isTooltipVisible = true;
};

function handleMouseLeave(ps) {
	ps.isHovered = false;
	ps.isTooltipVisible = false;
};

async function handleKeyDown(event, ps) { // Handle Keydown event on translation textarea.
	try {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();

			const isSuccess = await store.updateSingleSentence(projectId, ps);

			event.target.blur();
			ps.isNotSaved = true;

			if (isSuccess) {
				const result = await store.getSingleSentence(projectId, ps);

				const index1 = projectSentences.value.findIndex(item => item.sentence_id === result[0].sentence_id);
				const index2 = copiedProjectSentences.value.findIndex(item => item.sentence_id === result[0].sentence_id);

				if (index1 !== -1) {
					projectSentences.value[index1] = result[0];
				}
				if (index2 !== -1) {
					copiedProjectSentences.value[index2] = result[0];
				}
                ps.isNotSaved = false;
			}

		} else if (event.key === "Enter" && event.shiftKey) { 
			// shift + enter 줄바꿈
		}
	} catch (e) {
		console.error("handleKeyDown error: ", e);
	}
}

function isGloNotSaved (gd) {
	const compareTarget = copiedGlossaryData.value.find(item => item.id === gd.id);
	return gd.translated_term_latest !== compareTarget.translated_term_latest;
}

async function handleGloKeyDown(event, gd, index) { // Handle Keydown event on textarea. mainNode: cloned ProjectText
	try {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			event.target.blur();

			flags.value.savingGlo = index;
			const isPatched = await store.updateGlossaryData(projectId, gd);

			if (isPatched) {
				flags.value.savingGlo = -1;
				flags.value.applyEditedGlossaryData = true;

				const result = await store.getOneGloData(projectId, gd.id);

				const index1 = glossaryData.value.findIndex(item => item.id === result.id);
				const index2 = copiedGlossaryData.value.findIndex(item => item.id === result.id);

				if (index1 !== -1) {
					glossaryData.value[index1] = result;
				}
				if (index2 !== -1) {
					copiedGlossaryData.value[index2] = result;
				}
			}
		} else if (event.key === "Enter" && event.shiftKey) {
			// shift + enter 줄바꿈
		}
	} catch (e) {
		console.error("handleGloKeyDown error: ", e);
	}
}

function autoResizeGlo(gd) {
	nextTick(() => {
		const index = glossaryData.value.findIndex((item) => item === gd);
		const textarea = gloTextareaRefs.value[index];

		if (textarea) {
			textarea.style.height = "auto"; // 높이 초기화
			textarea.style.height = `${textarea.scrollHeight}px`; // 내용에 맞춰 높이 조정
		}
	});
}

function initProjectSentencesData() {
	projectSentences.value.forEach(item => {
		if (item.latest_text === null || item.latest_text === "") {
			item.latest_text = item.translated_text;
		}

		Object.assign(item, {
			isHovered: false,
			isTooltipVisible: false,
			loading: false,
			isNotSaved: false
		});
	});
}

function handleCopy(index, rIndex, reference) { // copy glossary
	navigator.clipboard.writeText(reference).catch(err => console.error("복사 실패", err));

	copiedReferenceTarget.value[0] = index;
	copiedReferenceTarget.value[1] = rIndex;

	setTimeout(() => {
		copiedReferenceTarget.value[0] = -1;
		copiedReferenceTarget.value[1] = -1;
	}, 3000);
}

async function handleAddGlossaryData() {
	try {
		if (gloTextOriginalInput.value.trim() === '') {
			alert(t('원문 용어를 입력해주세요.', 'Please enter the original glossary.'));
			return;
		} else if (gloTextTranslationInput.value.trim() === '') {
			alert(t('번역 용어를 입력해주세요.', 'Please enter the translated glossary.'));
			return;
		} else {
			const addedGloId = await store.createGlossaryData(projectId, gloTextOriginalInput.value, gloTextTranslationInput.value);
			
			if (addedGloId === undefined) {
				alert(t('중복된 용어가 있습니다.', 'There are duplicate terms.'));
				return;
			}
			
			const tempObj = {
				id: addedGloId,
				isFocused: false,
				is_deleted: false,
				reference: [],
				original_term: gloTextOriginalInput.value,
				translated_term: gloTextTranslationInput.value,
				translated_term_latest: gloTextTranslationInput.value,
				translation_project_id: projectId,
			}

			addedGlossaryData.value.push(tempObj);
			
			const resposeObj = await store.getOneGloData(projectId, parseInt(addedGloId));
			addedGlossaryData.value[addedGlossaryData.value.length-1].reference = resposeObj.reference;

			gloTextOriginalInput.value = '';
			gloTextTranslationInput.value = '';

			await getGloDataInfinite();
			syncLeftRightSection();
		}
	} catch (e) {
		console.error('handleAddGlossaryData error', e);
	}
}

async function copyToParaphrasedText(ps) {
	console.log(ps)
	ps.latest_text = ps.polished_text;
	console.log(ps)

	const isSuccess = await store.updateSingleSentence(projectId, ps);

	if (isSuccess) {
		const result = await store.getSingleSentence(projectId, ps);
		const index1 = projectSentences.value.findIndex(item => item.sentence_id === result[0].sentence_id);
		const index2 = copiedProjectSentences.value.findIndex(item => item.sentence_id === result[0].sentence_id);

		if (index1 !== -1) {
			projectSentences.value[index1] = result[0];
		}
		if (index2 !== -1) {
			copiedProjectSentences.value[index2] = result[0];
		}

		console.log(ps)
	}
}

async function deleteGloData(gd) {
	try {
		await store.deleteGlossaryData(projectId, gd.id)

		gd.is_deleted = true;
	} catch (e) {
		console.error("deleteGloData: ", e);
	}
}

async function initSearchSentences() {
	projectTextInput.value = ''
	preProjectTextInput.value = '';
	await syncTablesHeights();
}

async function syncTablesHeights(originalEl, translationEl) {
	await nextTick();
	resizeTextarea();
	originalEl.style.height = 'auto';
	translationEl.style.height = 'auto';

	const maxHeight = Math.max(originalEl.offsetHeight, translationEl.offsetHeight) - 24;
	originalEl.style.height = `${maxHeight}px`;
	translationEl.style.height = `${maxHeight}px`;
}

function syncLeftRightSection() {
	const left = document.getElementsByClassName('left_section')[0];
	const right = document.getElementsByClassName('right_section')[0];
	const max = Math.max(left.scrollHeight, right.scrollHeight);

	left.style.height = max + 'px';
	right.style.height = max + 'px';
}

function setTextareaRef(el, index) {
	if (el) textareaRefs.value[index] = el;
}

function resizeTextarea(event) {
	const textarea = event?.target;
	if (!textarea) return;

	// textarea 높이 조절
	textarea.style.height = 'auto';
	textarea.style.height = textarea.scrollHeight + 'px';

	// 해당 행의 높이만 다시 동기화
	nextTick(() => {
		const row = textarea.closest('.table_row');
		if (row) {
			const originalEl = row.querySelector('.original');
			const translationEl = row.querySelector('.translation');
			if (originalEl && translationEl) {
				syncTablesHeights(originalEl, translationEl);
			}
		}
	});
}

async function getGloDataInfinite() {
	try {
		const startPk = lastGloId.value === 1 ? lastGloId.value : lastGloId.value + 1;
		const result = await store.getGloDataInfinite(projectId, startPk);

		if (result.length === 0) {
			gloObserver.disconnect();
			return;
		}

		glossaryData.value = glossaryData.value.concat(result.gloArr);
		copiedGlossaryData.value = [];
		copiedGlossaryData.value = structuredClone(glossaryData.value);

		lastGloId.value = result.lastGloId;
	} catch (e) {
		console.error("getGloDataInfinite", e);
	}
}

function scrollToLoadingRow() {
	nextTick(() => {
		const loadingRow = document.querySelector('.table_header');
		if (loadingRow) {
			loadingRow.scrollIntoView({
				behavior: 'smooth',
				block: 'center', // 화면 중앙에 위치
				inline: 'nearest'
			});
		}
	});
}

async function runTranslation() {
	try {
		const resData = await store.runTranslation(projectId);
		
		if (resData.result) {
			await initialize();
			flags.value.isTranslated = true;

			setTimeout(() => {
				scrollToLoadingRow();
			}, 500);
		}
	} catch (e) {
		console.error("runTranslation error ", e);
	}
}

function getLoadingProgress() {
	return new Promise((resolve, reject) => {
		const checkProgress = async () => {
			try {
				
				const res = await api.get(ENDPOINTS.GET_NEW_PROJECT_LOADING + projectId);

				currentProgressNum.value = res.data.percentage;

				if (res.data.percentage < 100) {
					timeoutId.value = setTimeout(checkProgress, 3000);
				} else {
					clearTimeout(timeoutId.value);
					resolve(true);
				}
			} catch (e) {
				console.error("getLoadingProgress error:", e);
				reject(false);
			}
		};

		checkProgress();
	});
}

async function applyGlossaryDataToProject() {
	const result = await store.applyGlossaryDataToProject(projectId);
	flags.value.translated = true;

	await runTranslation();
	
	if (result) {
		await initialize();
	}
}

async function deleteProject(yn) { // false: cancel, true: delete
	try {
		if (!yn) {
			const target = document.querySelector('.confirm_container');
			target.classList.add('slide_up');

			setTimeout(() => {
				target.classList.remove('slide_up');
				flags.value.deleteProject = yn;
			}, 300)
			return
		}

		const isSuccess = await store.deleteProject(projectId);

		if (isSuccess) {
			alert(store.userLanguage !== 1 ? '프로젝트가 삭제 되었습니다.' : 'The project has been deleted.');
			await router.push({ name: 'ProjectList' });
		}
	} catch (e) {
		console.error("deleteProject", yn);
	}
}

async function initialize() {
	projectInfo.value = await store.getProjectInfo(projectId);

	const isLoaded = await getLoadingProgress();
	if (isLoaded) {
		projectSentences.value = await store.getProjectTexts(projectId, 0);
		initProjectSentencesData();
		copiedProjectSentences.value = structuredClone(projectSentences.value);

		if (projectSentences.value[0].translated_text !== "") {
			flags.value.isTranslated = true;
		}

		formData.value.tamseCode = projectInfo.value.tamse_code;
		formData.value.sourceLangId = projectInfo.value.source_language_id;
		formData.value.targetLangId = projectInfo.value.target_language_id;
		formData.value.publicPromptId = projectInfo.value.public_prompt_id;
		formData.value.translationModelId = projectInfo.value.translation_model_id;
		formData.value.have_private_prompt = projectInfo.value.have_private_prompt;
		formData.value.projectFieldId = projectInfo.value.project_field_id;
		formData.value.displayName = projectInfo.value.display_name;
		formData.value.documentType = projectInfo.value.document_type_id;

		nextTick(() => {
			originalTables.value.forEach((_, i) => {
				syncTablesHeights(originalTables.value[i], translationTables.value[i]);
			});
		});
	}

	await getGloDataInfinite();

	if (!store.languageListDefault.length) await store.getLanguageListDefault();
	if (!store.fieldListDefault.length) await store.getFieldListDefault();
	if (!store.translationModelListDefault.length) await store.getTranslationModelListDefault();

	srcLangName.value = langNameById(projectInfo.value.source_language_id);
	tgtLangName.value = langNameById(projectInfo.value.target_language_id);
	fieldName.value = fieldNameById(projectInfo.value.project_field_id);
	translationModelName.value = translationModelNameById(projectInfo.value.translation_model_id);

	syncLeftRightSection();

	if (localStorage.getItem('projectCreated') === '1') {
		await showModal(0);
	} else {
		flags.value.translated = true;
	}
}

function addMaxTableRender() {
	if (maxTableRender.value + 50 > projectSentences.value.length) {
		maxTableRender.value += (projectSentences.value.length - maxTableRender.value);
	} else if (maxTableRender.value >= projectSentences.value.length) {
		// none
	} else {
		maxTableRender.value += 50;
	}

	// sync table's height and section height
	nextTick(() => {
		originalTables.value.forEach((_, i) => {
			syncTablesHeights(originalTables.value[i], translationTables.value[i]);
		});
		syncLeftRightSection();
	});
}

// Optional modal logic
async function showModal(type) {
	try {
		flags.value.modalOnOff = true;

		if(type === 0) {
			flags.value.firstGloDetected = true;
		} else if (type === 1) {
			flags.value.promptSetting = true;
			await getPrivatePromptData();
			console.log(store.translationModelListDefault)
			console.log(projectInfo.value)
			console.log(projectInfo.value.translation_model_id)

			if (!Array.isArray(store.translationModelListDefault) || store.translationModelListDefault.length === 0) {
				await store.getTranslationModelListDefault();
				translationModelName.value = translationModelNameById(projectInfo.value.translation_model_id);
				formData.value.translationModelId = projectInfo.value.translation_model_id;
			} else {
				formData.value.translationModelId = projectInfo.value.translation_model_id;
			}
		}

	} catch (e) {
		console.error("showModal", e);
	}
}

function closeModal () {
	localStorage.setItem('projectCreated', '0');
	flags.value.modalOnOff = false;
	flags.value.firstGloDetected = false;
}

async function getPrivatePromptData() {
	try {
		
		const res = await api.get(ENDPOINTS.GET_PRIVATE_PROMPT_DATA + projectId);
		if (res.data) {
			formData.value.promptContents = res.data.result.prompt;
		}
	} catch (e) {
		console.error("getPrivatePromptData", e);
	}
}

async function updatePrivatePrompt() {
	try {
		formData.value.promptContents = formData.value.promptContents === '' ? ' ' : formData.value.promptContents;
		const res = await api.patch(ENDPOINTS.UPDATE_PRIVATE_PROMPT + projectId, {prompt: formData.value.promptContents})

		if (formData.value.promptContents === ' ') {
			await api.patch(ENDPOINTS.UPDATE_PROJECT_DATA + projectId, {have_private_prompt: false});
		}

		return res.data.result;
	} catch (e) {
		console.error("updatePrivatePrompt", e);
	}
}

async function createTranslationPartial(ps) {
	try {
		ps.loading = true;

		const rb = {
			translation_project_id: projectId,
			translation_column_id: ps.translation_column_id,
			sentence_id: ps.sentence_id,
			translation_model_id: projectInfo.value.translation_model_id
		}

		const isTranslated = await store.createTranslationPartial(rb);

		if (isTranslated) {
			const result = await store.getSingleSentence(projectId, ps);

			result.forEach(item => {
				if (item.latest_text === null || item.latest_text === "") {
					item.latest_text = item.translated_text;
				}

				Object.assign(item, {
					isHovered: false,
					isTooltipVisible: false,
					loading: false,
					isNotSaved: false
				});
			});

			const index1 = projectSentences.value.findIndex(item => item.sentence_id === result[0].sentence_id);
			const index2 = copiedProjectSentences.value.findIndex(item => item.sentence_id === result[0].sentence_id);
			
			if (index1 !== -1) {
				projectSentences.value[index1] = result[0];
			}
			if (index2 !== -1) {
				copiedProjectSentences.value[index2] = result[0];
			}

			ps.loading = false;
		}
	} catch (e) {
		console.error("createTranslationPartial error: ", e);
	}
}

async function translateWhole(target) { // target 0: Tranalation, 1: Compare Engine
	try {
		flags.value.modalOnOff = false;
		// flags.value.loadingCompareEngine = false;
		// flags.value.compareEngineOnOff = false;
		const rb = {
			public_prompt_id: formData.value.publicPromptId,
			public_polishing_prompt_id: projectInfo.value.public_polishing_prompt_id,
			have_private_prompt: true,
			display_name: projectInfo.value.display_name,
			document_type_id: projectInfo.value.document_type_id,
			// translation_model_comparing_id: 0,
			// is_cancelled: true
		}
		console.log('rb:', rb);
		
		const isUpdated = await store.updateProjectData(projectId, rb);
		console.log("isUpdated: ", isUpdated);
		console.log("aaa", await store.getProjectInfo(projectId));

		if (isUpdated) {
			const isPromptUpdated = await updatePrivatePrompt();
			console.log("isPromptUpdated: ", isPromptUpdated);

			if (isPromptUpdated) {
				projectSentences.value = [];
				copiedProjectSentences.value = [];

				const rb2 = {
					translation_project_id: projectId,
					translation_column_id: target,
					translation_model_id: formData.value.translationModelId
				}

				const result = await store.createTranslationWhole(rb2);
				if (result) {
					await initialize();
				}
			}
		}
	} catch (e) {
		console.error("translateWhole: ", e);
	}
}

async function updateParaphraseTextAll() {
	try {
		if (flags.value.paraphraseAll) {
			alert("윤문 내용을 반영 중입니다.");
			return
		}

		flags.value.paraphraseAll = true;
 
		let count = 0;

		for (let i = 0; i < projectSentences.value.length; i++) {
			projectSentences.value[i].latest_text = projectSentences.value[i].polished_text;
		}

		for (let i = 0; i < projectSentences.value.length; i++) {
			const isSuccess = await store.updateSingleSentence(projectId, projectSentences.value[i]);

			if (isSuccess) {
				await store.getSingleSentence(projectId, projectSentences.value[i]);
				count++;
			}
		}

		if (count === projectSentences.value.length) {
			flags.value.paraphraseAll = false;
		}

	} catch (e) {
		console.error("updateParaphraseTextAll", e);
	}
}

// 7. Watchers
watch(() => flags.value.isTranslated, () => {
	originalTables.value.forEach((_, idx) => {
		syncTablesHeights(originalTables.value[idx], translationTables.value[idx]);
	});
});

watch(projectTextInput, () => { // after search project sentences
	nextTick(() => {
		originalTables.value.forEach((_, i) => {
			syncTablesHeights(originalTables.value[i], translationTables.value[i]);
		});
	});
});

watch(() => projectSentences.value.map(ps => ps.latest_text), (newValues, oldValues) => {
	nextTick(() => {
		newValues.forEach((newValue, index) => {
			if (newValue !== oldValues?.[index]) {
				const textarea = textareaRefs.value[index];
				if (textarea) {
					textarea.style.height = 'auto';
					textarea.style.height = textarea.scrollHeight + 'px';
				}
			}
		});
	});
}, { deep: true });

watch(() => flags.value.modalOnOff, (isModalOpen) => {
	if (isModalOpen) {
		document.body.style.overflow = 'hidden';
		
		const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
		document.body.style.paddingRight = `${scrollbarWidth}px`;
	} else {
		document.body.style.overflow = '';
		document.body.style.paddingRight = '';
	}
});


// 8. Lifecycle Hooks
onBeforeMount(() => {
	// Optional setup
});

onMounted(async () => {
	if (!authStore.isAuthenticated) {
		await router.push({ name: "Login" });
		return;
	}

	await initialize();

	gloObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					getGloDataInfinite();
				}
			});
		},
		{ threshold: 0.1 }
	);

	if (gloObserverTarget.value) {
		gloObserver.observe(gloObserverTarget.value);
	}

	projectSentencesObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					addMaxTableRender();
				}
			});
		},
		{threshold: 0.1}
	);

	if (projectSentencesObserverTarget.value) {
		projectSentencesObserver.observe(projectSentencesObserverTarget.value);
	}
});

onUnmounted(() => {
	// Optional teardown
	if (gloObserver) {
		gloObserver.disconnect();
	}

	if (projectSentencesObserver) {
		projectSentencesObserver.disconnect();
	}
	document.body.style.overflow = '';
	document.body.style.paddingRight = '';
});
</script>

<template v-cloak>
	<div id="projectResult">
		<div class="sub_header">
			<div class="left">
				<i-chevron class="icon size_24 left grey" />
				<h3 class="project_name">{{ projectInfo.display_name }}</h3>
			</div>
			<div class="right">
				<button v-if="!flags.isTranslated" class="translation_btn btn size_40 primary" @click="runTranslation">
					{{ t("번역 실행하기", "Run translation") }}
				</button>
				<i-trash class="icon size_16 black_1" @click="flags.deleteProject = !flags.deleteProject"/>
				<div class="download_btn">
					<i-download class="icon size_16 black_1" :class="{ hovered: flags.downloadHover }"
						@mouseenter="flags.downloadHover = true" />
					<div class="download_hovered_wrapper" :class="{ download_hover: flags.downloadHover }"
						@mouseleave="flags.downloadHover = false">
						<p @click="
							store.downloadOriginalDocument(
								projectInfo.id,
								projectInfo.display_name
							)
							">
							{{ t("원본 파일", "Original file") }}
						</p>
						<p @click="
							store.downloadTranslationDocument(
								projectInfo.id,
								projectInfo.display_name
							)
							">
							{{ t("번역문 파일", "Translation file") }}
						</p>
						<p @click="
							store.downloadGlossaryDocument(
								projectInfo.id,
								projectInfo.display_name
							)
							">
							{{ t("용어집 파일", "Glossary file") }}
						</p>
					</div>
				</div>
				<i-setting class="icon size_16 black_1" @click="showModal(1)" />
			</div>
		</div>

		<div class="result_main">
			<!--Sentence-->
			<div class="left_section">
				<div class="top">
					<div class="project_info">
						<div class="language_info">
							<span class="src_language">
								{{ srcLangName }}
							</span>
							<i-chevron class="icon size_20 right" />
							<span class="tgt_language">
								{{ tgtLangName }}
							</span>
						</div>

						<span class="tamse_zone">
							{{ projectInfo.tamse_code }},
						</span>&nbsp;
						<transition name="fade" mode="out-in">
							<span class="field_name" :key="store.userLanguage">
								{{ t(`${fieldName} 분야`, `${fieldName}`) }}
							</span>
						</transition>
						<div v-if="projectInfo.is_cancelled" class="translation_cancelled"></div>
					</div>
				</div>

				<div class="table_control_panel">
					<p class="total_count" :key="store.userLanguage">{{ t(`${projectSentences.length}개의 문장`,
						`${projectSentences.length} ${projectSentences.length === 1 ? 'sentence' : 'sentences'}`) }}</p>

					<div v-if="flags.isTranslated" class="search_container">
						<i-search class="icon size_12 grey_3" />
						<input id="searchProjectText" v-model="preProjectTextInput" :placeholder="t('검색', 'Search')"
							@keydown.enter="projectTextInput = preProjectTextInput" />
						<div class="xmark_background" @click="initSearchSentences">
							<i-close class="icon size_12 grey_2" />
						</div>
					</div>
				</div>

				<div class="table_header">
					<div class="index">No</div>
					<div class="original" :class="{ 'is_translated': flags.isTranslated }">
						<p>{{ t('원문', 'Original') }}</p>
					</div>
					<div class="translation" :class="{ 'is_translated': flags.isTranslated }">
						<span class="engine_name">{{ translationModelName }}</span>&nbsp;
						<p>{{ t('번역문', 'Translation') }}</p>

						<div v-if="projectInfo.source_language_id === 2 && projectInfo.target_language_id === 1"
								class="icon_wrapper" @mouseenter="flags.paraphraseAllTooltip = true"
								@mouseleave="flags.paraphraseAllTooltip = false" @click="updateParaphraseTextAll()">
							<i-paraphrase v-if="!flags.paraphraseAll" class="icon size_12 white"/>
							<div v-else class="loading" style="width: 8px; height: 8px; padding: 2px;"></div>
							<transition name="fade" mode="out-in">
								<p v-if="flags.paraphraseAllTooltip" class="tooltip" :key="store.userLanguage">
									{{ t('윤문 전체 반영하기', 'Reflect the entire text') }}</p>
							</transition>
						</div>
					</div>
				</div>
				<div class="table_body">
					<template v-if="currentProgressNum < 100">
						<div class="loading_row">
							<p style="margin-bottom: 8px">{{ currentProgressNum }}%</p>
							<div class="loading"></div>
							<button v-if="!projectInfo.is_cancelled" class="btn size_20 primary translation_cancel_text"
								@click="stopTranslating2">{{ t('번역 중단하기', 'Stop Translation') }}

							</button>
							<button v-else disabled class="btn size_20 primary translation_cancel_text"
								style="margin-top: 12px">
								{{ t('프로세스 중단 중...', 'Aborting process...') }}
							</button>
						</div>
					</template>

					<template v-else>
						<div class="table_row" v-for="(ps, index) in filteredProjectSentences" :key="index">
							<template v-if="index < maxTableRender">
								<div class="index">
									<span>{{ String(ps.sentence_id + 1).padStart(2, '0') }}</span>
								</div>
								<div ref="originalTables" class="original"
									:class="{ 'is_translated': flags.isTranslated }">
									<span>{{ ps.original_text }}</span>
								</div>
								<div ref="translationTables" class="translation" :class="{ 'is_translated': flags.isTranslated }" @mouseenter="ps.isHovered = true" @mouseleave="checkTranslationPartialLoading(ps)">
									<span class="badge" :class="{
										edited: ps.isEdited || ps.latest_text !== ps.translated_text,
										not_saved: ps.latest_text !== copiedProjectSentences[ps.sentence_id]?.latest_text,
									}"></span>

									<div v-show="ps.isHovered" class="translate_partial_icon" @click="createTranslationPartial(ps)"
											@mouseenter="handleMouseEnter(ps)" @mouseleave="handleMouseLeave(ps)">
										<i-synchronize v-if="!ps.loading" class="icon size_16 grey_1"/>
										<div v-else class="loading" style="width: 10px; height: 10px; padding: 3px;"></div>
										<p v-show="ps.isTooltipVisible" class="tooltip">{{ t('다시 번역하기', 'Translate again') }}</p>
									</div>

									<textarea class="translated_text" :class="{ 'not_saved': ps.isNotSaved }" v-model="ps.latest_text" 
										@change="checkNotSavedStatus(ps)" @input="resizeTextarea" :ref="el => setTextareaRef(el, index)" @keydown="handleKeyDown($event, ps)"></textarea>
									<hr />
									<p v-if="projectInfo.source_language_id !== 2 && projectInfo.target_language_id !== 1" class="back_translated_text">
										{{ ps.translated_text_korean }}
									</p>
									<div v-else class="paraphrased_text" @mouseenter="flags.paraphraseCopy = ps.sentence_id" @mouseleave="flags.paraphraseCopy = null">
										<div v-show="flags.paraphraseCopy === ps.sentence_id" class="variant_partial_icon">
											<i-paraphrase v-if="flags.paraphraseCopy === ps.sentence_id" class="icon size_16 grey_1"
													@click="copyToParaphrasedText(ps)"
													@mouseenter="flags.paraphraseCopyTooltip = ps.sentence_id"
													@mouseleave="flags.paraphraseCopyTooltip = null"/>
											<div v-else class="loading" style="width: 10px; height: 10px; padding: 3px;"></div>
											<p v-show="flags.paraphraseCopyTooltip === ps.sentence_id" class="tooltip">
												{{ t('기존 번역문에 반영하기', 'Reflect on existing translations') }}
											</p>
										</div>
										<p style="margin-bottom: 4px">{{ t('윤문', 'Editing') }} :</p>
										<p>{{ ps.polished_text }}</p>
									</div>
								</div>
							</template>
						</div>
					</template>
				</div>
				<div id="projectSentencesObserverTarget" ref="projectSentencesObserverTarget"></div>

				<button v-if="!flags.isTranslated" class="translation_btn btn size_40 primary" @click="runTranslation">
					{{ t("번역 실행하기", "Run translation") }}
				</button>
			</div>

			<!--GLO-->
			<div class="right_section" :class="{ 'is_translated' : flags.isTranslated }">
				<div class="top">
					<transition name="fade" mode="out-in">
						<h4 :key="store.userLanguage">{{ t('용어집', 'Glossary') }}</h4>
					</transition>
					<transition name="fade" mode="out-in">
						<p class="description" :key="store.userLanguage">
							{{ t('용어 항목을 클릭하여 수정하거나 직접 추가/삭제할 수 있습니다.',
								'You can click on a term entry to edit it or add/delete it directly.') }}
						</p>
					</transition>
					<button v-if="flags.translated" class="btn size_21 blue reflect_btn" @click="applyGlossaryDataToProject">
						<span>{{ t(`번역문에 반영`, `Reflect in the translation`) }}</span>
						<i-synchronize class="icon size_12 blue" />
					</button>
					<div v-if="flags.isTranslated" class="glo_btn_container">
						<button class="btn size_32 grey" @click="flags.gloDeleteMode = !flags.gloDeleteMode">
							<i-trash v-show="!flags.gloDeleteMode" class="icon size_12 grey_2"/>
							<span v-show="!flags.gloDeleteMode">{{ t(`용어 삭제`, `Delete Glossary`) }}</span>
							
							<i-check v-show="flags.gloDeleteMode" class="icon size_12 grey_2"/>
							<span v-show="flags.gloDeleteMode">{{ t(`삭제 완료`, `Deletion complete`) }}</span>
						</button>
						<button class="btn size_32 blue add_glo_btn" :class="{ 'edit_mode' : flags.gloEditMode }" @click="flags.gloEditMode = !flags.gloEditMode ">
							<i-plus class="icon size_12 blue"/>
							<span>{{ t(`용어 추가`, `Add Glossary`) }}</span>
						</button>
					</div>
				</div>
				<div class="glo_table_header">
					<div>
						<transition name="fade" mode="out-in">
							<p :key="store.userLanguage">{{ t('원문 용어', 'Original term') }}</p>
						</transition>
					</div>
					<div>
						<transition name="fade" mode="out-in">
							<p :key="store.userLanguage">{{ t('번역된 용어', 'Translated term') }}</p>
						</transition>
					</div>
				</div>
				<div v-show="flags.gloEditMode || !flags.isTranslated" class="glo_table_header glo_search_row">
					<div class="input_box original_text">
						<transition name="fade" mode="out-in">
							<input id="originGloText" type="text" v-model="gloTextOriginalInput"
								:placeholder="t('추가할 원문 용어', 'Original text terms to add')" @keydown.enter="handleAddGlossaryData"/>
						</transition>
					</div>

					<div class="input_box translation_text">
						<transition name="fade" mode="out-in">
							<input id="translationGloText" type="text" v-model="gloTextTranslationInput"
								:placeholder="t('추가할 번역문 용어', 'Translated text terms to add')" @keydown.enter="handleAddGlossaryData"/>
						</transition>
						<i-plus class="icon size_12 blue" @click="handleAddGlossaryData"/>
					</div>
				</div>

				<div class="glo_table_row_wrapper">
					<div v-for="(agd, index) in filteredAddedGlossaryData" :key="agd.id" class="glo_table_row">
						<div class="just_added"></div>
						<template v-if="!agd.is_deleted">
							<div class="glo_original">
								<p>{{ agd.original_term }}</p>
							</div>
							<div class="glo_translation">
								<div class="contents" :class="{ 'edit_mode' : flags.gloEditMode || !flags.isTranslated || flags.gloDeleteMode }">
									<!-- badge -->
									<span v-if="flags.savingGlo === (agd.id - glossaryData[0].id)" class="badge loading"></span>
									<span v-else class="badge" :class="{
										edited: agd.translated_term_latest !== copiedGlossaryData[index]?.translated_term,
										not_saved: isGloNotSaved(agd),
									}"></span>

									<div class="glo_textarea_container" :class="{ 'focused': agd.isFocused }">
										<textarea class="glo_textarea" v-model="agd.translated_term_latest"
											@focus="agd.isFocused = true" @blur="agd.isFocused = false"></textarea>
										<span class="focus_msg" :class="{ 'on': agd.isFocused }">{{ t(`엔터를 눌러 저장하세요`, `Press Enter to save`)}}</span>
									</div>
									<div class="glo_recommend_wrapper" v-for="(reference, rIndex) in agd.reference"
										:key="rIndex" @click="handleCopy(index, rIndex, reference)">
										<span>{{ reference }}</span>
										<i-check
											v-if="copiedReferenceTarget[0] === index && copiedReferenceTarget[1] === rIndex"
											class="icon size_12 tamse_c1" />
										<i-copy v-else class="icon size_12 tamse_c1" />
									</div>
								</div>
								<div v-show="flags.gloEditMode || !flags.isTranslated || flags.gloDeleteMode" class="delete_container" @click="deleteGloData(agd)">
									<i-trash class="icon size_12 grey_3" />
								</div>
							</div>
						</template>
					</div>
					<div v-for="(gd, index) in glossaryData" :key="index" class="glo_table_row">
						<template v-if="!gd.is_deleted">
							<div class="glo_original">
								<p>{{ gd.original_term }}</p>
							</div>
							<div class="glo_translation">
								<div class="contents" :class="{ 'edit_mode' : flags.gloEditMode || !flags.isTranslated || flags.gloDeleteMode }">
									<!-- badge -->
									<span v-if="flags.savingGlo === (gd.id - glossaryData[0].id)" class="badge loading"></span>
									<span v-else class="badge" :class="{
										edited: gd.translated_term_latest !== copiedGlossaryData[index]?.translated_term,
										not_saved: isGloNotSaved(gd),
									}"></span>

									<div class="glo_textarea_container" :class="{ 'focused': gd.isFocused }">
										<textarea ref="gloTextareaRefs" class="glo_textarea" :class="{ 'not_saved': isGloNotSaved(gd) }" v-model="gd.translated_term_latest"
											@keydown="handleGloKeyDown($event, gd, index)" @input="autoResizeGlo(gd)"
											@focusin="flags.isGloFocused = (gd.id - glossaryData[0].id)"
											@focusout="flags.isGloFocused = null"
											@focus="gd.isFocused = true" @blur="gd.isFocused = false"></textarea>
										<span class="focus_msg" :class="{ 'on': gd.isFocused }">{{ t(`엔터를 눌러 저장하세요`, `Press Enter to save`)}}</span>
									</div>

									<div class="glo_recommend_wrapper" v-for="(reference, rIndex) in gd.reference"
										:key="rIndex" @click="handleCopy(index, rIndex, reference)">
										<span>{{ reference }}</span>
										<i-check
											v-if="copiedReferenceTarget[0] === index && copiedReferenceTarget[1] === rIndex"
											class="icon size_12 tamse_c1" />
										<i-copy v-else class="icon size_12 tamse_c1" />
									</div>
								</div>
								<div v-show="flags.gloEditMode || !flags.isTranslated || flags.gloDeleteMode" class="delete_container" @click="deleteGloData(gd)">
									<i-trash class="icon size_12 grey_3" />
								</div>
							</div>
						</template>
					</div>
				</div>
				<div id="gloObserverTarget" ref="gloObserverTarget"></div>
			</div>
		</div>

		<!-- modals -->
		<div v-if="flags.modalOnOff" class="modal_wrapper">
			<!-- Setting Prompt -->
			<div v-if="flags.promptSetting" class="modal_container">
				<div class="modal_header">
					<h3>{{ t('커스텀 프롬프트 설정', 'Set up custom prompts') }}</h3>
					<i-close class="icon size_20 grey_2 close-button" @click="flags.modalOnOff = false"/>
				</div>

				<form class="form_group">
					<label for="engine">{{ t('번역 엔진', 'Translation Engine') }}</label>
					<select id="engine" v-model="formData.translationModelId">
						<option value="" disabled>{{ t('선택', 'Select') }}</option>
						<option v-for="tmld in store.translationModelListDefault.filter(tmld => !tmld.is_deprecated)" :key="tmld" :value="tmld.id">{{ tmld.model_name }}</option>
					</select>

					<!--분야-->
					<label for="field">{{ t('분야', 'Field') }} <span>{{ formData.projectFieldId }}</span></label>
					<select id="field" v-model="projectInfo.project_field_id">
						<option value="" disabled>{{ t('선택', 'Select') }}</option>
						<option v-for="fld in store.fieldListDefault" :key="fld" :value="fld.id">{{ fld.name }}</option>
					</select>

					<label for="promptName">{{ t('프롬프트(기업)', 'Prompt (corporate)') }} <span>{{ formData.promptName
						}}</span></label>
					<select v-model="formData.promptContents">
						<option v-show="formData.promptContents === ''" value="" disabled>
							{{ t('프롬프트명을 선택하세요', 'Select a prompt name') }}
						</option>
						<option v-for="pl in store.publicPromptListDefault" :key="pl" :value="pl.prompt">{{ pl.name }}</option>
					</select>

					<label for="promptContents">{{ t('프롬프트 내용', 'Prompt Content') }}</label>
					<textarea v-model="formData.promptContents"
							:placeholder="t('프롬프트 내용을 입력하세요', 'Please enter the prompt')"></textarea>
				</form>

				<div class="warning_container">
					<img src="@/assets/img/warning.png" alt="warning"/>
					<p>
						{{ t('설정 변경 후 재번역 시, 번역문에 적용된 모든 수정 내용이 초기화됩니다.',
							'When retranslating after changing settings, all edits applied to the translation will be reset.') }}</p>
				</div>

				<button class="btn size_28 primary" @click="translateWhole(0)">{{ t('반영하기', 'Reflect') }}</button>
			</div>

			<!-- Delete Project -->
			<div v-if="flags.deleteProject" class="confirm_container">
				<transition name="fade" mode="out-in">
					<p>{{ t('프로젝트를 삭제 할까요?', 'Are you sure you want to delete the project?') }}</p>
				</transition>
				<div class="btn_wrapper">
					<button class="btn size_28 grey" @click="deleteProject(false)">
						<transition name="fade" mode="out-in">
							<span :key="store.userLanguage">{{ t('취소', 'Cancel') }}</span>
						</transition>
					</button>
					<button class="btn size_28 red" @click="deleteProject(true)">
						<transition name="fade" mode="out-in">
							<span :key="store.userLanguage">{{ t('삭제하기', 'Delete') }}</span>
						</transition>
					</button>
				</div>
			</div>

			<!-- First enter the project -->
			<div v-if="flags.firstGloDetected" class="modal_container">
				<h4 class="detected_title">
					{{ t(`우측의 용어가 검색되었습니다. 용어 검토가 완료되면 번역 실행하기 버튼을 눌러 주세요.`, 'Terminology found on the right. Please click the Run Translation button when the terminology review is complete.') }}
				</h4>
				<p class="description">
					{{ t('용어 항목을 클릭하여 수정하거나 직접 추가/삭제할 수 있습니다.', 'You can modify it by clicking on the terminology item or add/delete it yourself.') }}
				</p>
				<button class="btn size_28 primary confirm_btn" @click="closeModal">{{ t('확인', 'Confirm') }}</button>
			</div>
		</div>
	</div>
</template>